import { useSupportSheet } from "../../components/SupportSheet";
import { memo, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  SuccessIcon,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { DEFERRED_ORIGINATION_ROUTES, TITLE_COLOR } from "../../constants";
import { LegalModal } from "../../components/legal/LegalModal";
import { Popup } from "../../components/Popup";
import {
  getDeferredOriginationMockReceipt,
  type DeferredOriginationMockReceipt,
} from "../../gallery/deferredOriginationMockReceipts";
import { amountFormatter, formatHeroAmount, formatLabeledAmount } from "../../utils";
import { CheckIcon, CopyIcon, LimitInfoIcon, StatusCircleIcon, StatusFailedIcon, StatusPendingIcon } from "../../assets/icons";
import {
  shareOrDownloadBlob,
  shouldOfferNativeShare,
} from "../../utils/shareReceipt";
import { buildReceiptPdf } from "../../utils/receiptPdf";
import {
  availableReceiptDate,
  buildReceiptRows,
  formatReceiptDateTime,
  referenceLabel,
  splitVisibleReferences,
} from "../../utils/receiptRows";
import {
  STATUS_TONE_COLOR,
  deferredBindingTone,
  toStatusPresentation,
  type StatusTone,
} from "../../utils/statusPresentation";
import "../../styles/legal.style.css";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import {
  buildShareText,
  pickShareFirstName,
  shareOrCopyMessage,
} from "../../utils/shareMessage";
import { useDeferredOriginationStore } from "../../store/useDeferredOriginationStore";
import { Navbar } from "../../components/Navbar";


const statusIcon = (tone: StatusTone, color: string) => {
  switch (tone) {
    case "completed":
      return <SuccessIcon size={10} colors={[color, color]} />;
    case "failed":
    case "expired":
      return <StatusFailedIcon size={10} color={color} />;
    case "initial":
      return <StatusPendingIcon size={10} color={color} />;
    case "progress":
    default:
      return <StatusCircleIcon size={10} color={color} />;
  }
};

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate);
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const money = (amount: number | undefined, currency: string): string =>
  formatLabeledAmount(amount ?? 0, currency);

const bindingStatusKey = (
  status: DeferredOriginationMockReceipt["bindingStatus"]
): string => {
  switch (status) {
    case "awaiting_claim":
      return "receipt.statusAwaitingClaim";
    case "payment_bound":
      return "receipt.statusPaymentBound";
    case "payout_bound":
      return "receipt.statusPayoutBound";
    case "executable":
      return "receipt.statusExecutable";
    case "funded":
      return "receipt.statusCompleted";
    case "failed":
      return "receipt.statusFailed";
    case "expired":
      return "receipt.statusExpired";
    case "cancelled":
      return "receipt.statusCancelled";
    default:
      return "receipt.statusPending";
  }
};

/**
 * Sender-side deferred origination receipt (gallery).
 */
export const ReceiptSummaryPage = memo(() => {
  const { t, i18n } = useTranslation("deferred-origination");
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { mintResult } = useDeferredOriginationStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("deferred-origination");
  const location = useLocation();
  const { originationId: originationIdParam } = useParams<{
    originationId: string;
  }>();
  const [showTransactionPolicyModal, setShowTransactionPolicyModal] =
    useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [copiedType, setCopiedType] = useState<string | null>(null);
  const sharingRef = useRef(false);
  const offerNativeShare = useMemo(
    () => shouldOfferNativeShare("application/pdf"),
    []
  );
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? "en").split("-")[0];

  const colorConfig = theme.whiteView;
  const {
    bodyCopy,
    dropzoneColors,
    title,
    footerColor,
    background,
    backgroundBtn,
    textColorBtn,
  } = colorConfig;
  const detail = useMemo(
    () =>
      getDeferredOriginationMockReceipt(
        originationIdParam,
        location.pathname
      ),
    [originationIdParam, location.pathname]
  );

  const canPayAtStore =
    detail.fundingMethod === "CASH_DEPOSIT" &&
    (detail.fundingStatus === "awaiting_funding" ||
      detail.fundingStatus === "barcode_issued");

  const canShareClaim =
    !canPayAtStore &&
    !detail.operationId &&
    (detail.bindingStatus === "awaiting_claim" ||
      detail.bindingStatus === "payment_bound") &&
    Boolean(detail.claimUrl);

  const operationStatus = (detail.status ?? "").toLowerCase();
  const canDownloadReceipt =
    !canPayAtStore &&
    (operationStatus === "completed" ||
      operationStatus === "committed" ||
      operationStatus === "funded");

  const handleShareClaim = async () => {
    if (!detail.claimUrl) return;
    const mintedSenderFirstName =
      mintResult?.originationId === detail.originationId
        ? mintResult.senderFirstName
        : undefined;
    const message = buildShareText(t, {
      firstName: pickShareFirstName(
        mintedSenderFirstName,
        session.data?.userData?.name?.firstName
      ),
      amount: detail.sendAmount ?? detail.amount,
      currency: detail.sourceCurrency,
      url: detail.claimUrl,
      language: i18n.language,
    });
    await shareOrCopyMessage(message, {
      title: t("share.shareTitle"),
      fallbackPrompt: t("share.manualPrompt"),
    });
  };

  const handleShare = async () => {
    if (sharingRef.current) return;
    sharingRef.current = true;
    try {
      const [first, ...rest] = (detail.recipientName ?? "").split(" ");
      const receiptOp = {
        operationId: detail.operationId ?? detail.originationId,
        status: detail.status ?? detail.bindingStatus,
        sendAmount: detail.sendAmount ?? detail.amount ?? 0,
        receiveAmount: detail.receiveAmount ?? 0,
        exchangeRate: detail.exchangeRate ?? 0,
        fee: detail.fee,
        fundingFee: detail.fundingFee,
        transactionFee: detail.transactionFee,
        taxFee: detail.taxFee,
        totalCost: detail.totalDebit ?? detail.sendAmount ?? 0,
        sourceCurrency: detail.sourceCurrency ?? "USD",
        destinationCurrency: detail.destinationCurrency,
        createdAt: detail.createdAt,
        references: detail.references,
        recipient: {
          name: { firstName: first, lastName: rest.join(" ") },
        },
      };
      const { prominent } = splitVisibleReferences(detail.references);
      const blob = await buildReceiptPdf({
        operation: receiptOp,
        rows: buildReceiptRows(receiptOp, t, { lang }),
        prominentRefs: prominent,
        strings: {
          documentTitle: t("receipt.documentTitle"),
          youTransferred:
            detail.bindingStatus === "awaiting_claim"
              ? t("receipt.youSentPending")
              : t("receipt.youSent"),
          transactionId: t("receipt.originationId"),
          footerNote: t("receipt.footerNote"),
        },
        t,
        lang,
      });
      await shareOrDownloadBlob({
        blob,
        fileName: t("receipt.shareFileName", {
          id: detail.originationId ?? "recibo",
        }),
        title: t("receipt.shareTitle"),
        mediaType: "application/pdf",
      });
    } finally {
      sharingRef.current = false;
    }
  };

  const renderContent = () => {
    const hasOperation =
      detail.operationId != null &&
      detail.receiveAmount != null &&
      detail.sourceCurrency != null;

    const destination = detail.destinationCurrency;
    const source = detail.sourceCurrency ?? "USD";
    const heroAmount = detail.sendAmount ?? detail.amount ?? 0;
    const { prefix, suffix, intPart: heroInt, decPart: heroDec } = formatHeroAmount(
      heroAmount,
      source
    );

    const bindingLabel = t(bindingStatusKey(detail.bindingStatus));
    const opPresentation = hasOperation && detail.status
      ? toStatusPresentation(detail.status)
      : null;
    const statusTone: StatusTone =
      opPresentation?.tone ?? deferredBindingTone(detail.bindingStatus);
    const statusValue = opPresentation
      ? t(opPresentation.labelKey)
      : bindingLabel;
    const statusColor = STATUS_TONE_COLOR[statusTone];

    const summaryItems: {
      label: string;
      value: string;
      isBold?: boolean;
      statusColor?: string;
      icon?: React.ReactNode;
    }[] = hasOperation
      ? [
          {
            label: t("receipt.status"),
            value: statusValue,
            isBold: true,
            statusColor,
            icon: statusIcon(statusTone, statusColor),
          },
          {
            label: t("receipt.amountSent"),
            value: money(detail.sendAmount ?? detail.amount, source),
          },
          {
            label: t("receipt.transactionFee"),
            value: money(detail.transactionFee, source),
          },
          {
            label: t("receipt.exchangeRate"),
            value: `${amountFormatter(detail.exchangeRate ?? 0, destination, {
              country: "en-US",
              minimumFractionDigits: 4,
              maximumFractionDigits: 4,
            })} ${destination} / ${source}`,
          },
          {
            label: t("receipt.totalPayment"),
            value: money(detail.totalDebit, source),
          },
          {
            label: t("receipt.amountReceived"),
            value: money(detail.receiveAmount, destination),
          },
          {
            label: t("receipt.fundingFee"),
            value: money(
              detail.fundingFee ?? detail.fee ?? detail.totalFee,
              source
            ),
          },
          {
            label: t("receipt.transferTaxes"),
            value: money(detail.taxFee, source),
          },
          ...(detail.recipientName
            ? [
                {
                  label: t("receipt.recipient"),
                  value: detail.recipientName,
                },
              ]
            : []),
          {
            label: t("receipt.transactionDate"),
            value: formatReceiptDateTime(detail.createdAt),
          },
          {
            label: t("receipt.dateAvailable"),
            value: availableReceiptDate(detail.createdAt),
          },
        ]
      : [
          {
            label: t("receipt.status"),
            value: statusValue,
            isBold: true,
            statusColor,
            icon: statusIcon(statusTone, statusColor),
          },
          {
            label: t("receipt.amountSent"),
            value: money(detail.sendAmount ?? detail.amount, source),
          },
          {
            label: t("receipt.totalDebit"),
            value: money(detail.totalDebit, source),
          },
          ...(detail.recipientName
            ? [
                {
                  label: t("receipt.recipient"),
                  value: detail.recipientName,
                },
              ]
            : []),
          {
            label: t("receipt.dateAvailable"),
            value: formatDate(detail.createdAt),
          },
        ];

    const heroLabel =
      detail.bindingStatus === "awaiting_claim"
        ? t("receipt.youSentPending")
        : t("receipt.youSent");

    const idLabel = hasOperation
      ? t("receipt.transactionId")
      : t("receipt.originationId");
    const idValue = hasOperation
      ? (detail.operationId ?? detail.originationId)
      : detail.originationId;

    return (
      <section className="receipt-summary-container">
        <section className="receipt-summary-scroll">
          <ScrollFadeIndicator color={bodyCopy} />
        <section className="summary-section">
          <section className="receipt-card-capture">
          <section className="summary-title">
            <BodyCopy textColor={bodyCopy}>{heroLabel}</BodyCopy>
            <GradientText as="h1">
              {prefix}{heroInt}.<label>{heroDec}</label>
              {suffix ? (
                <span className="receipt-hero-currency">{suffix}</span>
              ) : null}
            </GradientText>
            <BodyCopy
              className="receipt-trx-caption"
              textColor={bodyCopy}
              aria-label={idLabel}
            >
              {idValue.slice(0, 8).toUpperCase()}
            </BodyCopy>
          </section>
          {splitVisibleReferences(detail.references).prominent.map((ref) => (
            <section
              key={`${ref.type}:${ref.value}`}
              className="receipt-reference-hero"
              style={{ borderColor: "#eeeeee" }}
            >
              <BodyCopy textColor={bodyCopy}>
                {referenceLabel(ref.type, ref.label, t, lang)}
              </BodyCopy>
              <div className="receipt-reference-value-wrap">
                <p className="receipt-reference-value" style={{ color: TITLE_COLOR }}>
                  {ref.value}
                </p>
                <button
                  type="button"
                  className="receipt-reference-copy"
                  style={{ color: bodyCopy }}
                  aria-label={
                    copiedType === ref.type
                      ? t("receipt.copied")
                      : t("receipt.copy")
                  }
                  onClick={() => {
                    void navigator.clipboard.writeText(ref.value);
                    setCopiedType(ref.type);
                  }}
                >
                  {copiedType === ref.type ? (
                    <CheckIcon size={16} />
                  ) : (
                    <CopyIcon size={16} />
                  )}
                </button>
              </div>
            </section>
          ))}
          <section className="summary-info">
            {summaryItems.map(({ label, value, isBold, statusColor: color, icon }, index) => (
              <SummaryInfoItem
                key={index}
                status={label}
                value={value}
                isBold={isBold}
                statusColor={color}
                icon={icon}
              />
            ))}
          </section>
          </section>
        </section>
        <section className="receipt-summary-disclaimers">
          <BodyCopy className="footer-text" textColor={footerColor}>
            {t("receipt.footerNote")}
          </BodyCopy>
          <BodyCopy className="footer-text">
            <GradientText
              as="button"
              type="button"
              className="receipt-cancel-policy"
              textColor={title}
              onClick={() => setShowTransactionPolicyModal(true)}
            >
              {t("receipt.cancelationPolicy")}
            </GradientText>
          </BodyCopy>
        </section>
        </section>
      </section>
    );
  };

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="need-help-header receipt-summary-section"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(DEFERRED_ORIGINATION_ROUTES.HOME)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="receipt-summary-content">{renderContent()}</Layout.Content>
        <Layout.Footer>
          <section className="footer-section transactions-footer">
            {detail.cancellation?.allowed && (
              <BodyCopy className="footer-text">
                <button
                  type="button"
                  className="receipt-cancel-transfer"
                  style={{ color: TITLE_COLOR }}
                  onClick={() => setShowConfirm(true)}
                >
                  {t("receipt.cancelTransfer")}
                </button>
              </BodyCopy>
            )}
            {canPayAtStore && (
              <section className="receipt-share-cta">
                <Button
                  size="large"
                  background={backgroundBtn}
                  color={textColorBtn}
                >
                  {t("receipt.payAtStore")}
                </Button>
              </section>
            )}
            {canShareClaim && (
              <section className="receipt-share-cta">
                <Button
                  size="large"
                  background={backgroundBtn}
                  color={textColorBtn}
                  onClick={() => void handleShareClaim()}
                >
                  {t("share.share")}
                </Button>
              </section>
            )}
            {canDownloadReceipt && (
            <section className="receipt-share-cta">
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                onClick={() => void handleShare()}
              >
                {offerNativeShare
                  ? t("receipt.shareReceipt")
                  : t("receipt.downloadReceipt")}
              </Button>
            </section>
            )}
          </section>
        </Layout.Footer>
      </AuraLayout>
      <LegalModal
        doc="transactionPolicy"
        visible={showTransactionPolicyModal}
        onClose={() => setShowTransactionPolicyModal(false)}
      />
      <Popup visible={showConfirm} onClose={() => setShowConfirm(false)} showClose={false}>
        <section className="cancel-confirm-slot">
          <LimitInfoIcon
            className="cancel-confirm-icon"
            size={60}
            colors={[TITLE_COLOR, TITLE_COLOR]}
          />
          <Title
            title={t("receipt.cancelConfirmTitle")}
            align="center"
            color={TITLE_COLOR}
          />
          <BodyCopy textColor={bodyCopy}>
            {t(
              detail.cancellation?.mode === "void"
                ? "receipt.cancelConfirmMessageVoid"
                : "receipt.cancelConfirmMessageRequest"
            )}
          </BodyCopy>
          <div className="cancel-confirm-actions">
            <Button
              size="large"
              variant="outline"
              background={backgroundBtn}
              color={TITLE_COLOR}
              style={{ backgroundColor: background }}
              onClick={() => setShowConfirm(false)}
            >
              {t("receipt.cancelConfirmYes")}
            </Button>
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              onClick={() => setShowConfirm(false)}
            >
              {t("receipt.cancelConfirmNo")}
            </Button>
          </div>
        </section>
      </Popup>
      {supportSheet}
    </>
  );
});

interface SummaryInfoItemProps {
  isBold?: boolean;
  statusColor?: string;
  status: string;
  value: string | React.ReactNode;
  icon?: React.ReactNode;
}

const SummaryInfoItem: React.FC<SummaryInfoItemProps> = ({
  isBold,
  status,
  value,
  statusColor,
  icon,
}) => (
  <section className={`summary-info-item ${isBold ? "bold" : ""} `}>
    <p>{status}</p>
    <p
      className={icon ? "summary-info-status" : undefined}
      style={{
        ...(statusColor ? { color: statusColor } : undefined),
        whiteSpace: "pre-line",
        textAlign: "right",
      }}
    >
      {icon}
      {value}
    </p>
  </section>
);
